import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import type { InlineEditMeta, ListFeature } from '../types';

export interface UseInlineEditOptions<T extends { id: string }> {
  onSave: (draft: Record<string, unknown>, rowId: string | null) => Promise<void>;
  onDelete: (row: T) => void;
  emptyDraft?: Record<string, unknown>;
  addLabel?: string;
}

export interface UseInlineEditReturn<T extends { id: string }> {
  meta: InlineEditMeta;
  feature: ListFeature<T>;
  pendingRow: (T & { id: 'new' }) | null;
  editingRowId: string | null;
  isSaving: boolean;
  startAdd: () => void;
}

export function useInlineEdit<T extends { id: string }>(
  options: UseInlineEditOptions<T>,
): UseInlineEditReturn<T> {
  const { onSave, emptyDraft = {}, addLabel } = options;
  const { t } = useTranslation('listView');

  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [isSaving, setIsSaving] = useState(false);

  const updateDraft = (key: string, value: unknown) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const startEdit = (rowId: string, rowData: Record<string, unknown>) => {
    setEditingRowId(rowId);
    setDraft(rowData);
  };

  const startAdd = () => {
    setEditingRowId('new');
    setDraft(emptyDraft);
  };

  const cancelEdit = () => {
    setEditingRowId(null);
    setDraft({});
  };

  const save = async () => {
    if (!editingRowId) return;
    setIsSaving(true);
    await onSave(draft, editingRowId === 'new' ? null : editingRowId)
      .then(() => {
        setEditingRowId(null);
        setDraft({});
      })
      .finally(() => setIsSaving(false));
  };

  const meta: InlineEditMeta = {
    editingRowId,
    draft,
    updateDraft,
    startEdit,
    cancelEdit,
    save,
    isSaving,
  };

  const feature: ListFeature<T> = {
    id: 'inline-edit',
    renderToolbar: () => (
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={startAdd}
        disabled={editingRowId !== null}
        data-testid="list-view-add-button"
      >
        <Plus className="h-4 w-4 mr-1" />
        {addLabel ?? t('add')}
      </Button>
    ),
  };

  const pendingRow =
    editingRowId === 'new' ? ({ id: 'new', ...emptyDraft } as T & { id: 'new' }) : null;

  return { meta, feature, pendingRow, editingRowId, isSaving, startAdd };
}
